import type { PostMeta } from "../posts";

export const meta: PostMeta = {
  slug: "igidr-mumbai-vs-dse-ma-economics-2026",
  title: "IGIDR Mumbai vs DSE: The Ultimate MA Economics 2026 Comparison Guide",
  description: "Exhaustive deep-dive comparing IGIDR Mumbai and Delhi School of Economics (DSE) for MA Economics in 2026. Covers curriculum, placements, PhD prospects, campus life, and preparation strategies.",
  pubDate: "2026-07-28",
  updatedDate: "2026-07-28",
  authorSlug: "uday-kumar",
  category: "careers",
  tags: ["igidr", "dse", "ma-economics", "placements", "phd-pathways", "cuet-pg"],
  readMinutes: 26,
  heroEyebrow: "Careers · Post-Graduate Admissions",
};

export function Body() {
  return (
    <>
      <p>
        Choosing between the Indira Gandhi Institute of Development Research (IGIDR) in Mumbai and the Delhi School of Economics (DSE) is arguably the most consequential decision an economics graduate in India will make. Both institutes represent the absolute pinnacle of economic training in South Asia, yet they offer fundamentally different academic philosophies, campus experiences, and career trajectories. This exhaustive 2026 guide breaks down everything from semester-wise curricula and elite placement statistics to international PhD placement records and the CUET-PG entrance examination.
      </p>

      {/* ─── Section 1: Curriculum ─── */}
      <h2>1. Detailed Curriculum Comparison: Semester-by-Semester</h2>
      <p>
        DSE is famously rigorous, often termed a "math bootcamp" in its first semester, with a heavy emphasis on theoretical microeconomics and pure econometrics. IGIDR, funded by the Reserve Bank of India, balances theoretical rigor with applied macroeconomics, finance, and development policy.
      </p>

      <h3>1.1. Core Course Comparison (Semesters 1-2)</h3>
      <div className="overflow-x-auto my-6">
        <table className="w-full text-left text-sm border-collapse border border-rule">
          <thead>
            <tr className="bg-card font-semibold border-b border-rule">
              <th className="p-3 border-r border-rule">Course Area</th>
              <th className="p-3 border-r border-rule">DSE Courses (Sem 1-2)</th>
              <th className="p-3">IGIDR Courses (Sem 1-2)</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-rule">
            <tr>
              <td className="p-3 border-r border-rule font-medium">Microeconomics</td>
              <td className="p-3 border-r border-rule">Micro Theory I (Axiomatic Consumer Theory, Production Theory, General Equilibrium) + Micro Theory II (Game Theory, Information Economics, Auction Theory)</td>
              <td className="p-3">Micro I (Applied Consumer &amp; Producer Theory, Market Structures) + Micro II (Game Theory, Mechanism Design)</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">Macroeconomics</td>
              <td className="p-3 border-r border-rule">Macro Theory I (Solow, Ramsey-Cass-Koopmans, OLG Models) + Macro Theory II (Dynamic Optimization, DSGE Foundations)</td>
              <td className="p-3">Macro I (IS-LM-BP, Growth Theory, Monetary Policy) + Macro II (Open Economy Macro, DSGE Introduction)</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">Mathematics</td>
              <td className="p-3 border-r border-rule">Mathematical Methods (Real Analysis, Topology, Fixed Point Theorems, Linear Algebra — extremely proof-heavy)</td>
              <td className="p-3">Mathematics for Economists (Calculus, Optimization, Linear Algebra — applied focus with less emphasis on proofs)</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">Econometrics</td>
              <td className="p-3 border-r border-rule">Econometric Methods I &amp; II (Asymptotic Theory, Matrix Algebra, MLE, GMM)</td>
              <td className="p-3">Econometrics I &amp; II (Time Series focus, Panel Data, Applied Financial Econometrics)</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">Unique Courses</td>
              <td className="p-3 border-r border-rule">None in core — all unique courses are electives</td>
              <td className="p-3">Energy and Environment Economics (unique to IGIDR, reflecting its development research mandate)</td>
            </tr>
          </tbody>
        </table>
      </div>

      <h3>1.2. Elective Course Comparison (Semesters 3-4)</h3>
      <div className="overflow-x-auto my-6">
        <table className="w-full text-left text-sm border-collapse border border-rule">
          <thead>
            <tr className="bg-card font-semibold border-b border-rule">
              <th className="p-3 border-r border-rule">Elective Domain</th>
              <th className="p-3 border-r border-rule">DSE Offerings</th>
              <th className="p-3">IGIDR Offerings</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-rule">
            <tr>
              <td className="p-3 border-r border-rule font-medium">Finance</td>
              <td className="p-3 border-r border-rule">Financial Economics, International Finance</td>
              <td className="p-3">Corporate Finance, Financial Derivatives, Portfolio Management, Risk Management</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">Development</td>
              <td className="p-3 border-r border-rule">Development Economics, Health Economics</td>
              <td className="p-3">Development Policy, Natural Resource Economics, Climate Change Economics, Agricultural Economics</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">Quantitative</td>
              <td className="p-3 border-r border-rule">Advanced Econometrics, Bayesian Methods, Machine Learning for Economists</td>
              <td className="p-3">Applied Econometrics, Financial Econometrics, Computational Economics</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">Public Policy</td>
              <td className="p-3 border-r border-rule">Public Economics, Taxation Theory</td>
              <td className="p-3">Public Finance, Regulatory Economics, Infrastructure Economics</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* ─── Section 2: Campus Life ─── */}
      <h2>2. Campus Life: Mumbai Goregaon vs Delhi North Campus</h2>
      <div className="overflow-x-auto my-6">
        <table className="w-full text-left text-sm border-collapse border border-rule">
          <thead>
            <tr className="bg-card font-semibold border-b border-rule">
              <th className="p-3 border-r border-rule">Aspect</th>
              <th className="p-3 border-r border-rule">IGIDR (Mumbai, Goregaon East)</th>
              <th className="p-3">DSE (Delhi, North Campus)</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-rule">
            <tr>
              <td className="p-3 border-r border-rule font-medium">Infrastructure</td>
              <td className="p-3 border-r border-rule">14-acre lush, secluded campus. World-class RBI-funded library. Fully air-conditioned single-occupancy hostels for all.</td>
              <td className="p-3">Heritage building in bustling North Campus. The legendary Ratan Tata Library. Hostel accommodation is extremely limited and competitive.</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">Cohort Size</td>
              <td className="p-3 border-r border-rule">Intimate (~40-45 students). Highly personalized faculty-student ratio of approximately 1:4.</td>
              <td className="p-3">Massive (~250-300 students). Highly competitive, sink-or-swim environment with a faculty-student ratio of approximately 1:15.</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">Vibe</td>
              <td className="p-3 border-r border-rule">Corporate, professional, serene. Close to Mumbai's financial hub (BKC is 15 km away). Feels like a research retreat.</td>
              <td className="p-3">Politically active, vibrant, intellectually intense. Surrounded by iconic North Campus cafes (Tom Uncle's, Baba Ke Paranthe) and DU culture.</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">Hostel Availability</td>
              <td className="p-3 border-r border-rule font-semibold text-grade">Guaranteed for all students. Single-occupancy AC rooms.</td>
              <td className="p-3">Extremely limited. Most students must find private PGs in Hudson Lane, Vijay Nagar, or Kamla Nagar (Rs 8,000-15,000/month).</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">Computing Facilities</td>
              <td className="p-3 border-r border-rule">Dedicated computer lab with STATA, MATLAB, R Studio, EViews licenses for all students. 24/7 access.</td>
              <td className="p-3">Computer Centre available but shared across all DU departments. Students often rely on personal laptops.</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">Library</td>
              <td className="p-3 border-r border-rule">IGIDR Library: comprehensive economics and finance collection, JSTOR/Elsevier institutional access, quiet study carrels.</td>
              <td className="p-3">Ratan Tata Library: one of Asia's finest social science libraries. Unmatched historical collection. Often crowded during exam season.</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* ─── Section 3: Stipend & Financial ─── */}
      <h2>3. Stipend, Fellowship &amp; Financial Support Comparison</h2>
      <div className="overflow-x-auto my-6">
        <table className="w-full text-left text-sm border-collapse border border-rule">
          <thead>
            <tr className="bg-card font-semibold border-b border-rule">
              <th className="p-3 border-r border-rule">Financial Parameter</th>
              <th className="p-3 border-r border-rule">IGIDR</th>
              <th className="p-3">DSE</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-rule">
            <tr>
              <td className="p-3 border-r border-rule font-medium">Monthly Stipend</td>
              <td className="p-3 border-r border-rule font-semibold text-grade">Rs 12,000-15,000/month (RBI-funded fellowship)</td>
              <td className="p-3">None by default. Some students qualify for UGC NET-JRF (Rs 31,000/month) or GATE fellowship.</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">Tuition Fees</td>
              <td className="p-3 border-r border-rule">Nominal (Rs 5,000-10,000 per semester)</td>
              <td className="p-3">Nominal (Rs 5,000-8,000 per semester for DU internal; Rs 15,000-20,000 for external)</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">Hostel + Mess</td>
              <td className="p-3 border-r border-rule">Subsidized (Rs 3,000-5,000/month including meals)</td>
              <td className="p-3">Private PG cost: Rs 8,000-15,000/month + food expenses</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">Net Cost of Attendance</td>
              <td className="p-3 border-r border-rule font-semibold text-grade">Near zero or positive (stipend exceeds expenses)</td>
              <td className="p-3">Rs 1.5-2.5 lakh per year (out-of-pocket for non-JRF students)</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* ─── Section 4: Placements ─── */}
      <h2>4. Placement Statistics &amp; Corporate Roles (2025-2026 Cycle)</h2>
      <p>
        Both institutes offer phenomenal ROI, but they cater to slightly different corporate appetites. IGIDR dominates the banking, risk, and core finance sectors due to its RBI pedigree. DSE dominates consulting, analytics, and data science due to its massive alumni base and quantitative rigor.
      </p>
      <div className="overflow-x-auto my-6">
        <table className="w-full text-left text-sm border-collapse border border-rule">
          <thead>
            <tr className="bg-card font-semibold border-b border-rule">
              <th className="p-3 border-r border-rule">Placement Metric</th>
              <th className="p-3 border-r border-rule">IGIDR 2025-26</th>
              <th className="p-3">DSE 2025-26</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-rule">
            <tr>
              <td className="p-3 border-r border-rule font-medium">Median CTC</td>
              <td className="p-3 border-r border-rule font-semibold">Rs 17-19 LPA</td>
              <td className="p-3 font-semibold">Rs 16-18 LPA</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">Highest CTC</td>
              <td className="p-3 border-r border-rule">~Rs 32 LPA</td>
              <td className="p-3">~Rs 35 LPA</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">Placement Rate</td>
              <td className="p-3 border-r border-rule">~95% (small batch, near-100% corporate placement)</td>
              <td className="p-3">~75-80% (many students opt for PhDs, civil services, or self-placement)</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">Top Recruiters</td>
              <td className="p-3 border-r border-rule">RBI, J.P. Morgan, Citi, ICICI Bank, Barclays, Goldman Sachs (Mumbai desk)</td>
              <td className="p-3">Bain &amp; Co, McKinsey Knowledge Center, Nomura, American Express, Fractal Analytics, EY</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">Dominant Role Types</td>
              <td className="p-3 border-r border-rule">Quantitative Analyst, Treasury Strategist, Risk Modeler, Credit Analyst</td>
              <td className="p-3">Data Scientist, Business Analyst, Management Trainee, Research Associate</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* ─── Section 5: PhD Pathways ─── */}
      <h2>5. PhD Progression &amp; International Pathways</h2>
      <p>
        If your goal is a fully-funded PhD in Economics at a Top-20 US university (MIT, Harvard, Stanford, Chicago, Princeton), DSE has a massive historical advantage. The recommendation letters from DSE professors — who themselves hold Ivy League PhDs and publish in Econometrica and the AER — carry immense weight globally.
      </p>
      <p>
        IGIDR has rapidly closed this gap in the last decade. IGIDR's smaller batch size means students can easily secure research assistantships (RAs) with faculty, leading to strong, personalized LORs.
      </p>

      <h3>5.1. International PhD Placement Records</h3>
      <div className="overflow-x-auto my-6">
        <table className="w-full text-left text-sm border-collapse border border-rule">
          <thead>
            <tr className="bg-card font-semibold border-b border-rule">
              <th className="p-3 border-r border-rule">University Tier</th>
              <th className="p-3 border-r border-rule">DSE Alumni Placements</th>
              <th className="p-3">IGIDR Alumni Placements</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-rule">
            <tr>
              <td className="p-3 border-r border-rule font-medium">Top 10 (MIT, Harvard, Stanford, Princeton, Chicago, Yale, Berkeley, Columbia, Penn, Northwestern)</td>
              <td className="p-3 border-r border-rule font-semibold text-grade">Regular placements (5-10 students per cycle). DSE has the strongest pipeline to MIT and Chicago.</td>
              <td className="p-3">Occasional (1-3 per cycle). Improving rapidly. Strong connections to UC San Diego and Minnesota.</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">Top 11-30 (UCLA, Michigan, Wisconsin, Cornell, Duke, Brown, NYU)</td>
              <td className="p-3 border-r border-rule">Strong presence (10-15 students per cycle).</td>
              <td className="p-3">Growing presence (5-8 students per cycle).</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">European PhD (LSE, Oxford, Cambridge, Toulouse, Barcelona GSE)</td>
              <td className="p-3 border-r border-rule">Moderate (3-5 per cycle). DSE-LSE connection is historically strong.</td>
              <td className="p-3">Moderate (2-4 per cycle). IGIDR has good connections with European development research institutes.</td>
            </tr>
          </tbody>
        </table>
      </div>

      <h3>5.2. IGIDR Direct Integrated PhD Pathway</h3>
      <p>
        IGIDR offers a unique M.Phil./PhD integrated programme that allows meritorious M.Sc. students to transition directly into doctoral work without taking a separate PhD entrance exam. Students who maintain a CGPA of 7.0 or higher during the M.Sc. programme can apply for this direct pathway. The PhD carries a JRF-equivalent stipend of Rs 31,000/month (under HRDG-CSIR norms).
      </p>

      <div className="my-6 border border-grade bg-card p-5 text-sm">
        <strong className="mono-label text-grade">The Third Alternative: ISI (Kolkata/Delhi)</strong>
        <p className="mt-2">If your pure math skills are Olympiad-level, the Indian Statistical Institute (ISI) Master of Science in Quantitative Economics (MSQE) program is arguably tougher to crack and yields even better US PhD placements than DSE. Its batch size is microscopic (15-20 students). ISI's entrance exam (JRF) tests real analysis, measure theory, and probability at a level that most economics undergraduates find daunting. However, the career outcomes for ISI graduates — whether in academia or quantitative finance — are unmatched in India.</p>
      </div>

      {/* ─── Section 6: Entrance Exam ─── */}
      <h2>6. Entrance Examination Pattern &amp; Sample Questions</h2>
      <p>
        Admission to both DSE and IGIDR is now primarily routed through the CUET-PG (COQP10) framework, though IGIDR sometimes maintains additional interview rounds or math tests depending on the year's policy.
      </p>

      <h3>6.1. CUET PG COQP10 Pattern</h3>
      <ul>
        <li><strong>Total Questions:</strong> 100 MCQs in 120 minutes</li>
        <li><strong>Marks:</strong> 300 total (3 per correct, -1 per incorrect)</li>
        <li><strong>Syllabus:</strong> Microeconomics (25-30%), Macroeconomics (25-30%), Mathematical Methods (15-20%), Statistics/Econometrics (10-15%), Indian Economy (10-15%)</li>
        <li><strong>DSE Cutoff (UR):</strong> 245-255 out of 300</li>
        <li><strong>IGIDR Cutoff (UR):</strong> 220-235 out of 300 (lower due to smaller applicant pool awareness)</li>
      </ul>

      <h3>6.2. Sample Entrance Questions</h3>
      <div className="space-y-4 my-6 text-sm">
        <div className="border border-rule bg-card p-4">
          <strong className="block text-base mb-1">Micro Sample Q:</strong>
          <p>A consumer has utility function U(x, y) = x^(1/3) * y^(2/3). Income = Rs 120, Px = 4, Py = 2. Find the optimal consumption bundle. What happens to the demand for good x if Px rises to 6?</p>
          <p className="text-pencil mt-2"><em>Expected Answer: x* = M/(3Px) = 120/12 = 10, y* = 2M/(3Py) = 240/6 = 40. When Px rises to 6: x* = 120/18 = 6.67.</em></p>
        </div>
        <div className="border border-rule bg-card p-4">
          <strong className="block text-base mb-1">Macro Sample Q:</strong>
          <p>In a closed economy IS-LM model: C = 100 + 0.8Yd, I = 200 - 20r, G = 100, T = 50, Md/P = 0.5Y - 50r, Ms/P = 500. Find the equilibrium Y and r.</p>
          <p className="text-pencil mt-2"><em>Expected Answer: IS curve: Y = 1700 - 100r. LM curve: Y = 1000 + 100r. Solving: r = 3.5, Y = 1350.</em></p>
        </div>
        <div className="border border-rule bg-card p-4">
          <strong className="block text-base mb-1">Math Sample Q:</strong>
          <p>Maximize f(x, y) = 4x + 3y subject to 2x^2 + 3y^2 = 72 using the Lagrange method. Find the maximum value.</p>
          <p className="text-pencil mt-2"><em>Expected Answer: Set up Lagrangian L = 4x + 3y - lambda(2x^2 + 3y^2 - 72). FOCs: 4 = 4*lambda*x, 3 = 6*lambda*y. Solving: x = 1/lambda, y = 1/(2*lambda). Substituting into constraint, solving for lambda, then finding f*.</em></p>
        </div>
      </div>

      {/* ─── Section 7: 6-Month Study Plan ─── */}
      <h2>7. The 6-Month Preparation Plan</h2>
      <div className="space-y-3 my-6">
        <div className="border border-rule bg-card p-4">
          <strong className="block text-base mb-1">Month 1-2: Mathematical Foundations</strong>
          <p className="text-sm">You cannot survive the exam without absolute mastery of calculus and linear algebra. Read Sydsaeter and Hammond cover to cover. Practice limits, continuity, constrained optimization (Lagrange, Kuhn-Tucker), and matrix operations. Solve at least 20 optimization problems daily.</p>
        </div>
        <div className="border border-rule bg-card p-4">
          <strong className="block text-base mb-1">Month 3-4: Advanced Micro &amp; Macro</strong>
          <p className="text-sm">Micro: Hal Varian's "Intermediate Microeconomics" (Chapters 1-36). Focus heavily on consumer theory, producer theory, and market structures. Solve all end-of-chapter problems. Macro: Blanchard and Froyen. Focus on IS-LM, AD-AS, Solow growth models, and Mundell-Fleming. Practice 15 numerical problems daily.</p>
        </div>
        <div className="border border-rule bg-card p-4">
          <strong className="block text-base mb-1">Month 5: Statistics &amp; Econometrics</strong>
          <p className="text-sm">Devore for Statistics (probability distributions, hypothesis testing, confidence intervals). Gujarati for basic Econometrics (OLS, multicollinearity, heteroscedasticity). Focus on computational problems rather than proofs at this stage.</p>
        </div>
        <div className="border border-rule bg-card p-4">
          <strong className="block text-base mb-1">Month 6: Mock Tests &amp; PYQ Papers</strong>
          <p className="text-sm">The final 30 days must be dedicated entirely to solving past 10 years' DSE, JNU, and IGIDR question papers under strict timed conditions. Analyze every error. Identify weak topics and do targeted revision. Take at least 1 full-length mock daily in the final 2 weeks.</p>
        </div>
      </div>

      {/* ─── Section 8: Faculty Research Areas ─── */}
      <h2>8. Faculty Research Strengths</h2>
      <div className="overflow-x-auto my-6">
        <table className="w-full text-left text-sm border-collapse border border-rule">
          <thead>
            <tr className="bg-card font-semibold border-b border-rule">
              <th className="p-3 border-r border-rule">Research Domain</th>
              <th className="p-3 border-r border-rule">DSE Faculty Strength</th>
              <th className="p-3">IGIDR Faculty Strength</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-rule">
            <tr>
              <td className="p-3 border-r border-rule font-medium">Microeconomic Theory</td>
              <td className="p-3 border-r border-rule font-semibold text-grade">World-class. Multiple faculty with Econometrica/AER/JET publications.</td>
              <td className="p-3">Strong. Applied focus on mechanism design and industrial organization.</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">Development Economics</td>
              <td className="p-3 border-r border-rule">Good. Some faculty work on RCTs and Indian development policy.</td>
              <td className="p-3 font-semibold text-grade">Excellent. IGIDR's founding mandate. Poverty, agriculture, and environment research.</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">Financial Economics</td>
              <td className="p-3 border-r border-rule">Moderate.</td>
              <td className="p-3 font-semibold text-grade">Strong. RBI connections enable access to proprietary financial data.</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">Econometrics</td>
              <td className="p-3 border-r border-rule font-semibold text-grade">Excellent. Theoretical econometrics with Bayesian and frequentist approaches.</td>
              <td className="p-3">Good. Applied time series and panel data focus.</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* ─── Section 9: Final Verdict ─── */}
      <h2>9. Final Verdict &amp; Decision Matrix</h2>
      <div className="overflow-x-auto my-6">
        <table className="w-full text-left text-sm border-collapse border border-rule">
          <thead>
            <tr className="bg-card font-semibold border-b border-rule">
              <th className="p-3 border-r border-rule">If Your Priority Is...</th>
              <th className="p-3">Choose</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-rule">
            <tr>
              <td className="p-3 border-r border-rule">Top US PhD placement (MIT, Harvard, Chicago)</td>
              <td className="p-3 font-semibold">DSE (stronger LOR network and historical track record)</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule">Guaranteed hostel + stipend (zero financial burden)</td>
              <td className="p-3 font-semibold">IGIDR (RBI-funded fellowship, free AC hostel)</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule">Investment banking / quantitative finance in Mumbai</td>
              <td className="p-3 font-semibold">IGIDR (Mumbai proximity, RBI brand, finance-heavy curriculum)</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule">Consulting / data science / analytics (Delhi NCR/pan-India)</td>
              <td className="p-3 font-semibold">DSE (massive alumni network, consulting recruiter pipeline)</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule">Personalized faculty attention and small-batch learning</td>
              <td className="p-3 font-semibold">IGIDR (40 students vs 300)</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule">Vibrant campus life, cultural activities, and DU experience</td>
              <td className="p-3 font-semibold">DSE (North Campus is unmatched for college life in India)</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule">RBI Grade B DEPR / public sector research career</td>
              <td className="p-3 font-semibold">IGIDR (direct RBI pipeline, many alumni in DEPR)</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* ─── Section 10: Related Guides ─── */}
      <h2>10. Related Academic &amp; Career Guides</h2>
      <p>
        Before you can worry about master's degrees, you must secure an excellent undergraduate CGPA. Navigate your DU curriculum using these companion guides:
      </p>
      <ul>
        <li><a href="/blog/du-ge-sec-vac-course-selection-2026" className="font-semibold underline text-grade">DU Electives Selection Guide 2026</a></li>
        <li><a href="/blog/how-to-calculate-sgpa-to-cgpa-in-du-2026" className="font-semibold underline text-grade">SGPA to CGPA Calculator Guide 2026</a></li>
        <li><a href="/blog/cobb-douglas-production-function-proofs" className="font-semibold underline text-grade">Cobb-Douglas Production Function Proofs</a></li>
        <li><a href="/blog/mundell-fleming-model-fixed-vs-floating" className="font-semibold underline text-grade">Mundell-Fleming Model Masterclass</a></li>
        <li><a href="/blog/cuet-pg-economics-2026-cutoff-dse-jnu" className="font-semibold underline text-grade">CUET PG Economics 2026 Cutoff Breakdown</a></li>
        <li><a href="/blog/rbi-grade-b-depr-2026-preparation-guide" className="font-semibold underline text-grade">RBI Grade B DEPR 2026 Preparation Guide</a></li>
        <li><a href="/blog/is-ma-economics-worth-it" className="font-semibold underline text-grade">Is an MA in Economics Worth It?</a></li>
      </ul>
    </>
  );
}


